import { api } from './client.js';
import type {
  UserDto,
  TokenDto,
  CreatedTokenDto,
  TemplateDto,
  TemplateSummaryDto,
  DocumentRequestDto,
  OAuthSessionDto,
} from '../types.js';

export const usersApi = {
  list: () => api.get<{ users: UserDto[] }>('/users'),
  get: (id: string) => api.get<{ user: UserDto }>(`/users/${id}`),
  create: (data: { username: string; email: string; password: string; role: 'admin' | 'user' }) =>
    api.post<{ user: UserDto }>('/users', data),
  update: (id: string, data: Partial<{ email: string; role: 'admin' | 'user'; isActive: boolean; password: string }>) =>
    api.patch<{ user: UserDto }>(`/users/${id}`, data),
  delete: (id: string) => api.delete<void>(`/users/${id}`),
};

export const tokensApi = {
  list: () => api.get<{ tokens: TokenDto[] }>('/tokens'),
  create: (data: { name: string; expiresAt?: number }) =>
    api.post<{ token: CreatedTokenDto }>('/tokens', data),
  revoke: (id: string) => api.delete<void>(`/tokens/${id}`),
};

export const templatesApi = {
  list: (q?: string) =>
    api.get<{ templates: TemplateSummaryDto[] }>(`/templates${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id: string) => api.get<{ template: TemplateDto }>(`/templates/${id}`),
  create: (data: unknown) => api.post<{ template: TemplateDto }>('/templates', data),
  update: (id: string, data: unknown) => api.put<{ template: TemplateDto }>(`/templates/${id}`, data),
  archive: (id: string) => api.post<{ ok: boolean }>(`/templates/${id}/archive`, {}),
  htmlUrl: (id: string) => `/api/templates/${id}/html`,
};

export const documentsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<{ documents: DocumentRequestDto[]; total: number }>(`/documents${qs}`);
  },
  get: (id: string) => api.get<{ document: DocumentRequestDto }>(`/documents/${id}`),
  generate: (data: { templateId: string; values: Record<string, unknown>; fileName?: string; metadata?: Record<string, string> }) =>
    api.post<{ document: { requestId: string; fileName: string; fileSize: number; contentHash: string; downloadUrl: string } }>('/documents', data),
  downloadUrl: (id: string) => `/api/documents/${id}/download`,
};

export const oauthSessionsApi = {
  list: () => api.get<{ sessions: OAuthSessionDto[] }>('/oauth/sessions'),
  revoke: (id: string) => api.delete<void>(`/oauth/sessions/${id}`),
};

export { authApi } from './auth.js';
